/**
 * Permanent account deletion via Cloud Function (Admin SDK).
 */
import {
  auth,
  firebaseSignOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  reauthenticateWithPopup,
} from '../auth/firebaseAuth';
import { purgeLegacyChatLocalStorage } from '../utils/legacyMockUsers';
import { chatReadStorageKey } from '../privateChat/constants';
import { clearGuestWalletMirror, walletStorageKey } from './walletService';
import { clearHomepageCache, HOMEPAGE_CACHE_KEY } from './homepageDataService';
import { PLAYER_STATS_STORAGE_KEY, playerStatsStorageKey } from '../utils/playerStats';

const AVATAR_STORAGE_KEY = 'lockedin_user_avatar';
const LOCKEDIN_BETS_KEY = 'lockedin_bets';

function friendsStorageKey(uid) {
  return `lockedin_friends_${uid}`;
}

function profileCacheKey(uid) {
  return `lockedin_profile_cache_${uid}`;
}

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

export function isAccountDeletionConfigured() {
  return Boolean(getFunctionsBaseUrl());
}

/** @returns {'password' | 'google' | 'other'} */
export function getAccountReauthMethod(user) {
  const providers = (user?.providerData || []).map((p) => p.providerId).filter(Boolean);
  if (providers.includes('password')) return 'password';
  if (providers.includes('google.com')) return 'google';
  return 'other';
}

/**
 * Re-authenticate before destructive actions (Firebase may require recent login).
 * @param {import('firebase/auth').User} user
 * @param {string} [password] — required for email/password accounts
 */
export async function reauthenticateForAccountDeletion(user, password) {
  if (!user) throw new Error('You must be signed in to delete your account.');

  const method = getAccountReauthMethod(user);
  if (method === 'password') {
    if (!password?.trim()) {
      throw new Error('Enter your password to confirm account deletion.');
    }
    if (!user.email) {
      throw new Error('This account has no email on file. Contact support to delete.');
    }
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    return;
  }

  if (method === 'google') {
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
    return;
  }

  throw new Error('Re-authentication is not supported for this sign-in method. Contact support.');
}

/**
 * Clear guest-sensitive localStorage before auth transitions or guest entry.
 * Does not purge chat/bets — callers handle those separately.
 * @param {string | null | undefined} previousUid
 */
export function clearGuestSensitiveLocalStorage(previousUid) {
  try {
    window.localStorage.removeItem(AVATAR_STORAGE_KEY);
    clearGuestWalletMirror();
    clearHomepageCache();
    if (previousUid) {
      window.localStorage.removeItem(profileCacheKey(previousUid));
      window.localStorage.removeItem(walletStorageKey(previousUid));
      const statsKey = playerStatsStorageKey(previousUid);
      if (statsKey) window.localStorage.removeItem(statsKey);
    }
  } catch (_) {
    // ignore quota / private mode errors
  }
}

/**
 * Remove cached local data for this user so a fresh signup starts clean.
 * @param {string | null | undefined} uid
 */
export function clearLocalUserSession(uid) {
  try {
    window.localStorage.removeItem(AVATAR_STORAGE_KEY);
    window.localStorage.removeItem(LOCKEDIN_BETS_KEY);
    window.localStorage.removeItem(PLAYER_STATS_STORAGE_KEY);
    purgeLegacyChatLocalStorage();
    clearGuestWalletMirror();
    window.localStorage.removeItem(HOMEPAGE_CACHE_KEY);
    if (uid) {
      window.localStorage.removeItem(friendsStorageKey(uid));
      window.localStorage.removeItem(walletStorageKey(uid));
      window.localStorage.removeItem(chatReadStorageKey(uid));
      window.localStorage.removeItem(profileCacheKey(uid));
      const statsKey = playerStatsStorageKey(uid);
      if (statsKey) window.localStorage.removeItem(statsKey);
    }
  } catch (_) {
    // ignore quota / private mode errors
  }
}

/**
 * Permanently delete the signed-in account (Firestore + Auth).
 * Caller should clear local session and navigate to signup afterward.
 * @param {string} [password] — required for email/password accounts
 */
export async function deleteUserAccountPermanently(password) {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to delete your account.');

  const base = getFunctionsBaseUrl();
  if (!base) {
    throw new Error('Account deletion is not configured. Contact support.');
  }

  await reauthenticateForAccountDeletion(user, password);

  const idToken = await user.getIdToken(true);
  const res = await fetch(`${base}/delete_account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Account deletion failed (${res.status})`);
  }

  try {
    await firebaseSignOut(auth);
  } catch (_) {
    // Auth user may already be removed server-side
  }
}
